import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { useId, type ReactNode } from 'react';
import { IconCheck } from './icons';
import { cn, disabledStyles, focusRing } from './utils';

export interface CheckboxProps {
  readonly checked: boolean | 'indeterminate';
  readonly onCheckedChange: (checked: boolean) => void;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
  readonly name?: string;
  readonly value?: string;
  readonly className?: string;
  /** Esconde o rótulo visualmente (uso em célula de tabela com cabeçalho). */
  readonly hideLabel?: boolean;
  readonly id?: string;
}

/**
 * Checkbox acessível.
 *
 * O rótulo é SEMPRE renderizado — quando `hideLabel` está ligado ele vira
 * `qc-sr-only` em vez de sumir. Um checkbox sem nome acessível numa matriz de
 * permissões (6 flags × N linhas) é indistinguível no leitor de tela, e é
 * exatamente esse o cenário da aba Distribuição.
 */
export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  disabled = false,
  name,
  value,
  className,
  hideLabel = false,
  id,
}: CheckboxProps) {
  const generatedId = useId();
  const controlId = id ?? `${generatedId}-checkbox`;
  const descriptionId = `${generatedId}-description`;

  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <CheckboxPrimitive.Root
        id={controlId}
        name={name}
        value={value}
        checked={checked}
        onCheckedChange={(next) => onCheckedChange(next === true)}
        disabled={disabled}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          'mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-sm border',
          'border-border-control bg-surface transition-colors',
          'data-[state=checked]:border-brand data-[state=checked]:bg-brand',
          'data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand',
          focusRing,
          disabledStyles,
        )}
      >
        <CheckboxPrimitive.Indicator className="text-brand-contrast">
          {checked === 'indeterminate' ? (
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              aria-hidden="true"
              focusable="false"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M2.5 6h7" />
            </svg>
          ) : (
            <IconCheck size={12} strokeWidth={3} />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>

      <div className={cn('min-w-0 flex-1', hideLabel && 'contents')}>
        <label
          htmlFor={controlId}
          className={cn(
            'cursor-pointer text-sm text-content',
            hideLabel && 'qc-sr-only',
            disabled && 'cursor-not-allowed opacity-55',
          )}
        >
          {label}
        </label>
        {description && !hideLabel ? (
          <p id={descriptionId} className="mt-0.5 text-xs text-content-subtle">
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}
